import { Permission, Role } from "appwrite";

import { getActiveProjectId } from "src/handlers/Projects";
import { safeParseJson } from "src/handlers/Projects/schema";
import { sanitizeId } from "src/handlers/utils";

// ---------------------------------------------------------------------------
// Device type constants
// ---------------------------------------------------------------------------

export const ROUTER_TYPE = "router";
export const SWITCH_TYPE = "switch";
export const SUPPORTED_DEVICE_TYPES = [ROUTER_TYPE, SWITCH_TYPE];

// Authoritative list of section keys that may live on a device tab. Used by
// `syncSection` to validate inputs and surfaced via the package re-exports
// because callers (notably the device editors) need it for validation too.
export const SECTION_KEYS = [
  "initial",
  "vlans",
  "dhcpsnooping",
  "switchports",
  "portchannels",
  "interfaces",
  "v6interfaces",
  "linterfaces",
  "dhcp",
  "dhcpexclusion",
  "dhcphelper",
  "hsrp",
  "vrrp",
  "staticroute",
  "ipv4acls",
  "ipv6acls",
  "prefixlists",
  "communitylists",
  "routemaps",
  "qosclassmaps",
  "qospolicymaps",
  "ospf",
  "v3ospf",
  "vrf",
  "eigrp",
  "namedeigrp",
  "bgp",
  "multicast",
  "multicastinterfaces",
  "ipsla",
  "tracks",
  "tunnels",
  "dynamicnat",
  "dynamicnatport",
  "staticnat",
  "basicsecurity",
  "advancedaaa",
  "localaaa",
  "urpf",
  "misc",
  "stp",
  "stpvlan",
  "portsecurity",
  "accessedge",
  "spansessions",
  "ntp",
  "ntpservers",
  "syslog",
  "syslogservers",
  "snmp",
  "snmpcommunities",
  "snmphosts",
  "snmpv3groups",
  "snmpv3users",
  "snmpv3views"
];

// ---------------------------------------------------------------------------
// JSON helpers
// ---------------------------------------------------------------------------

export function parseJson(value, fallback) {
  return safeParseJson(value, fallback);
}

export function stringify(value) {
  return JSON.stringify(value ?? null);
}

// ---------------------------------------------------------------------------
// Identity / context helpers
// ---------------------------------------------------------------------------

export function getProjectId() {
  return getActiveProjectId() || "";
}

export function getCurrentUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("userinfo"));
    return user?.$id || "";
  } catch {
    return "";
  }
}

// Presence rows are visible to every signed-in collaborator but only mutable
// by the user who owns the browser session creating the heartbeat.
export function getPresencePermissions() {
  const userId = getCurrentUserId();
  if (!userId) return undefined;

  return [
    Permission.read(Role.users()),
    Permission.update(Role.user(userId)),
    Permission.delete(Role.user(userId))
  ];
}

// ---------------------------------------------------------------------------
// Row id helpers (deterministic so writes are idempotent)
// ---------------------------------------------------------------------------

export function makeDeviceRowId(projectId, type, deviceId) {
  return sanitizeId(`${projectId}_${type}_${deviceId}`);
}

export function makePageRowId(projectId, pageKey) {
  return sanitizeId(`${projectId}_${pageKey}`);
}

export function makePresenceRowId(projectId, clientId) {
  return sanitizeId(`${projectId}_${clientId}`);
}

// ---------------------------------------------------------------------------
// Row payload builders
// ---------------------------------------------------------------------------

export function getDeviceLabel(type, tabId, tabData) {
  return tabData?.initial?.[0]?.hostname || `${type}-${tabId + 1}`;
}

export function buildDeviceRowData(projectId, type, tabId, tabData) {
  return {
    projectId,
    deviceType: type,
    deviceId: tabId,
    label: getDeviceLabel(type, tabId, tabData),
    dataJson: stringify(tabData),
    updatedAtMs: Date.now()
  };
}

export function buildPageRowData(projectId, pageKey, data) {
  return {
    projectId,
    pageKey,
    dataJson: stringify(data),
    updatedAtMs: Date.now()
  };
}
